import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, KeyRound, Eye, EyeOff, CheckCircle2, AlertTriangle } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import SEO from "../../components/SEO";
import Captcha from "./Captcha";

function Shell({ children, subtitle }) {
  return (
    <div className="grain flex min-h-screen items-center justify-center bg-navy px-4 py-10">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white p-8 shadow-2xl sm:p-10">
        <div className="flex flex-col items-center text-center">
          <img src="/logo.png" alt="Sahaj Sarathi Foundation logo" className="h-16 w-16 rounded-full bg-white object-contain ring-2 ring-saffron-500/50" />
          <h1 className="mt-4 font-display text-xl font-bold tracking-tight text-navy">SAHAJ SARATHI FOUNDATION</h1>
          <p className="mt-1 text-xs font-bold uppercase tracking-[0.2em] text-saffron-600">{subtitle}</p>
        </div>
        {children}
        <Link to="/superadmin/login" className="mt-6 block text-center text-xs font-semibold text-slate-400 hover:text-saffron-600" data-testid="back-to-login">← Back to Sign In</Link>
      </div>
    </div>
  );
}

export function ForgotPassword() {
  const [identity, setIdentity] = useState("");
  const [captcha, setCaptcha] = useState({ captcha_id: "", answer: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await api.post("/auth/forgot-password", { identity, captcha_id: captcha.captcha_id, captcha_answer: captcha.answer });
      setDone(r.data);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell subtitle="Forgot Password">
      <SEO title="Forgot Password" />
      {done ? (
        <div className="mt-8 text-center" data-testid="forgot-success">
          <CheckCircle2 className="mx-auto h-14 w-14 text-brandgreen" strokeWidth={1.5} />
          <p className="mt-4 text-sm leading-relaxed text-slate-600">
            If this account exists, a password reset link has been generated.
          </p>
          {!done.email_configured && (
            <p className="mt-4 rounded-lg bg-saffron-100/70 px-4 py-3 text-xs font-semibold text-slate-700" data-testid="forgot-email-notice">
              <AlertTriangle size={13} className="mr-1 inline" /> Email is <b>NOT CONFIGURED</b>, so the link could not be emailed.
              The site owner can configure SMTP in Superadmin → Site Content &amp; Settings → Email.
            </p>
          )}
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="forgot-form">
          <div>
            <label htmlFor="fp-identity" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Registered Email / Username / Mobile</label>
            <input id="fp-identity" data-testid="forgot-identity" required value={identity} onChange={(e) => setIdentity(e.target.value)}
              className="w-full rounded-md border border-gray-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-saffron-500/50" />
          </div>
          <Captcha onChange={setCaptcha} />
          {error && <p className="rounded-lg bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-600" data-testid="forgot-error">{error}</p>}
          <button type="submit" disabled={busy} className="btn-primary w-full disabled:opacity-50" data-testid="forgot-submit">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={15} />} Send Reset Link
          </button>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState({ password: "", confirm: "" });
  const [captcha, setCaptcha] = useState({ captcha_id: "", answer: "" });
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.password !== pw.confirm) { setError("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw.password, captcha_id: captcha.captcha_id, captcha_answer: captcha.answer });
      setDone(true);
      setTimeout(() => navigate("/superadmin/login"), 2000);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell subtitle="Reset Password">
      <SEO title="Reset Password" />
      {done ? (
        <div className="mt-8 text-center" data-testid="reset-success">
          <CheckCircle2 className="mx-auto h-14 w-14 text-brandgreen" strokeWidth={1.5} />
          <p className="mt-4 text-sm text-slate-600">Your new password is active immediately. Redirecting to sign in…</p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="reset-form">
          {!token && <p className="rounded-lg bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-600">Reset token missing from link.</p>}
          <div>
            <label htmlFor="rp-password" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">New Password (min 8 chars)</label>
            <div className="relative">
              <input id="rp-password" data-testid="reset-password" type={showPw ? "text" : "password"} required minLength={8} value={pw.password}
                onChange={(e) => setPw({ ...pw, password: e.target.value })}
                className="w-full rounded-md border border-gray-200 px-4 py-2.5 pr-11 text-sm outline-none focus:ring-2 focus:ring-saffron-500/50" />
              <button type="button" onClick={() => setShowPw(!showPw)} aria-label="Show or hide password" data-testid="reset-show-password"
                className="absolute right-3 top-2.5 text-slate-400 hover:text-navy">{showPw ? <EyeOff size={18} /> : <Eye size={18} />}</button>
            </div>
          </div>
          <div>
            <label htmlFor="rp-confirm" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Confirm Password</label>
            <input id="rp-confirm" data-testid="reset-confirm" type={showPw ? "text" : "password"} required value={pw.confirm}
              onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
              className="w-full rounded-md border border-gray-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-saffron-500/50" />
          </div>
          <Captcha onChange={setCaptcha} />
          {error && <p className="rounded-lg bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-600" data-testid="reset-error">{error}</p>}
          <button type="submit" disabled={busy || !token} className="btn-primary w-full disabled:opacity-50" data-testid="reset-submit">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={15} />} Set New Password
          </button>
        </form>
      )}
    </Shell>
  );
}
